import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { formatMoney } from 'helpers/helpers'
import { httpOk, GET, POST } from 'helpers/api'

const NEW_CATEGORY = 'new'

// Ширины шапки раздела фиксированные: иначе выпадающие списки стоят лесенкой
// под названиями разной длины
const SECTION_NAME_WIDTH = '240px'
const SECTION_FIELD_WIDTH = '260px'

function ExternalMenuPullModal({ show, onHide, externalGroupId, onApplied }) {
	const { t } = useTranslation()

	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [categories, setCategories] = useState([])
	const [sections, setSections] = useState([])
	const [pricing, setPricing] = useState(null)
	const [onlyNew, setOnlyNew] = useState(false)

	async function load() {
		setLoading(true)
		const response = await GET(`/external-menu-pull/${externalGroupId}`, {}, { loader: true })
		setLoading(false)

		if (httpOk(response)) {
			const data = response.data
			setCategories(data.categories || [])
			setSections((data.sections || []).map(section => ({
				...section,
				target: section.external_menu_id ?? NEW_CATEGORY,
				products: section.products.map(product => ({ ...product, sale_price: product.sale_price ?? '' })),
			})))
			setPricing(data.price ?? null)
			setOnlyNew(false)
		}
	}

	useEffect(() => {
		if (show) load()
		else setSections([])
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	const categoryOptions = useMemo(() => [
		{ id: NEW_CATEGORY, name: `+ ${t('external_menu_pull_new_category')}` },
		...categories,
	], [categories, t])

	const selectedCount = useMemo(() =>
		sections.reduce((sum, section) => sum + (canAdd(section) ? section.products.filter(p => p.selected).length : 0), 0),
		[sections]) // eslint-disable-line react-hooks/exhaustive-deps
	const newCount = useMemo(() =>
		sections.reduce((sum, section) => sum + section.products.filter(p => p.is_new).length, 0),
		[sections])
	const totalCount = useMemo(() =>
		sections.reduce((sum, section) => sum + section.products.length, 0),
		[sections])

	function updateSection(index, patch) {
		setSections(prev => prev.map((section, i) => i === index ? { ...section, ...patch } : section))
	}

	function updateProduct(sectionIndex, productId, patch) {
		setSections(prev => prev.map((section, i) => i !== sectionIndex ? section : {
			...section,
			products: section.products.map(p => p.product_id === productId ? { ...p, ...patch } : p),
		}))
	}

	function visibleProducts(section) {
		return onlyNew ? section.products.filter(p => p.is_new) : section.products
	}

	// Товар без раздела системного меню во внешнее меню не добавить: агрегатор такую
	// позицию не получит. Показываем, чтобы было видно, чего не хватает, но не даём отметить
	function canAdd(section) {
		return section.menu_id !== null && section.menu_id !== undefined
	}

	function toggleSection(sectionIndex, checked) {
		if (!canAdd(sections[sectionIndex])) return
		const visibleIds = new Set(visibleProducts(sections[sectionIndex]).map(p => p.product_id))
		setSections(prev => prev.map((section, i) => i !== sectionIndex ? section : {
			...section,
			products: section.products.map(p => visibleIds.has(p.product_id) ? { ...p, selected: checked } : p),
		}))
	}

	async function apply() {
		const payloadSections = sections
			.filter(section => canAdd(section))
			.map(section => ({
				menu_id: section.menu_id,
				external_menu_id: section.target === NEW_CATEGORY ? null : section.target,
				new_name: section.target === NEW_CATEGORY ? section.new_name : null,
				products: section.products
					.filter(p => p.selected)
					.map(p => ({ product_id: p.product_id, sale_price: p.sale_price === '' ? null : Number(p.sale_price) })),
			}))
			.filter(section => section.products.length > 0)
		if (payloadSections.length === 0) return

		setApplying(true)
		const response = await POST(`/external-menu-pull/${externalGroupId}`, {
			sections: payloadSections,
		}, { loader: true })
		setApplying(false)

		if (httpOk(response)) {
			toast.success(t('external_menu_pull_done', {
				products: response.data.added_products,
				categories: response.data.created_categories,
			}))
			onHide()
			onApplied && onApplied()
		}
	}

	return (
		<Modal show={show} animation={false} centered scrollable size="xl" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t('external_menu_pull')}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: '70vh' }}>
				<div className="text-muted mb-1">{t('external_menu_pull_hint')}</div>
				<div className="text-muted mb-3">
					{pricing?.matched > 0 && (pricing.markup > 0 || pricing.round)
						? t('external_menu_pull_price_guess', { markup: pricing.markup, round: pricing.round || 1 })
						: t('external_menu_pull_price_hint')
					}
				</div>

				{!loading && sections.length === 0 &&
					<div className="text-center text-muted py-5">{t('external_menu_pull_empty')}</div>
				}

				{sections.length > 0 &&
					<div className="form-check mb-3">
						<input id="pull-only-new" type="checkbox" className="form-check-input"
							checked={onlyNew}
							onChange={(e) => setOnlyNew(e.target.checked)} />
						<label className="form-check-label" htmlFor="pull-only-new">
							{t('external_menu_pull_only_new')} ({newCount} / {totalCount})
						</label>
					</div>
				}

				{sections.map((section, sectionIndex) => {
					const products = visibleProducts(section)
					if (products.length === 0) return null
					const addable = canAdd(section)
					const allChecked = addable && products.every(p => p.selected)
					const sectionName = section.menu_name || t('external_menu_pull_without_section')

					return (
						<div className="card mb-3" key={section.menu_id ?? 'none'}>
							<div className="card-header d-flex align-items-center gap-2 py-2">
								<input type="checkbox" className="form-check-input mt-0 flex-shrink-0"
									disabled={!addable}
									checked={allChecked}
									onChange={(e) => toggleSection(sectionIndex, e.target.checked)} />
								<div className="flex-shrink-0" style={{ width: addable ? SECTION_NAME_WIDTH : 'auto' }}>
									<div className="fw-bold text-truncate" title={sectionName}>{sectionName}</div>
									{!section.menu_active &&
										<div className="fz-12 text-muted">{t('external_menu_pull_section_off')}</div>
									}
									{!addable &&
										<div className="fz-12 text-danger">{t('external_menu_pull_no_section_hint')}</div>
									}
								</div>
								{addable && <i className="uil uil-arrow-right fz-18 flex-shrink-0"></i>}
								{addable && <div className="flex-shrink-0" style={{ width: SECTION_FIELD_WIDTH }}>
									<Select
										options={categoryOptions}
										value={categoryOptions.find(option => option.id === section.target)}
										onChange={(option) => updateSection(sectionIndex, { target: option.id })}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
										menuPortalTarget={document.body}
										styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
									/>
								</div>}
								{addable && section.target === NEW_CATEGORY &&
									<input type="text" className="form-control flex-shrink-0" style={{ width: SECTION_FIELD_WIDTH }}
										placeholder={t('name2')}
										value={section.new_name ?? ''}
										onChange={(e) => updateSection(sectionIndex, { new_name: e.target.value })} />
								}
							</div>
							<div className="table-responsive">
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ width: '33px' }}></th>
											<th>{t('name2')}</th>
											<th className="text-end" style={{ width: '15%' }}>{t('price')}</th>
											<th className="text-end" style={{ width: '180px' }}>{t('sale_price')}</th>
										</tr>
									</thead>
									<tbody>
										{products.map(product => (
											<tr key={product.product_id} className={addable ? 'cursor' : 'text-muted'}
												onClick={() => addable && updateProduct(sectionIndex, product.product_id, { selected: !product.selected })}>
												<td className="text-center">
													<input type="checkbox" className="form-check-input"
														disabled={!addable}
														checked={addable && !!product.selected}
														onChange={() => { }} />
												</td>
												<td>
													{product.name}
													{product.is_new &&
														<span className="badge bg-success ms-2">{t('external_menu_pull_new')}</span>
													}
												</td>
												<td className="text-end">{formatMoney(product.base_price)}</td>
												<td className="td-input td-input-editable" onClick={(e) => e.stopPropagation()}>
													<div className="td-editable">
														<i className="uil uil-pen td-editable-icon"></i>
														{/* Поправили цену — значит, позицию хотят добавить: отмечаем её */}
														<input type="number" min="0" className="form-control text-end"
															autoComplete="off"
															disabled={!addable}
															placeholder="—"
															value={product.sale_price}
															onChange={(e) => updateProduct(sectionIndex, product.product_id, { sale_price: e.target.value, selected: true })} />
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)
				})}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-warning" onClick={onHide}>
					{t('cancel')}
				</button>
				<button type="button" className="btn btn-primary"
					disabled={selectedCount === 0 || applying}
					onClick={apply}>
					{t('external_menu_pull_add', { count: selectedCount })}
				</button>
			</Modal.Footer>
		</Modal>
	)
}

export default ExternalMenuPullModal
